import OverlayBase from "../overlay/OverlayBase";
import React, {useEffect, useState} from "react";
import {ConsoleFilter, ConsoleFilterError, ConsoleFilterType, PostConsoleFilterResponse} from "../interfaces";
import {useLocalStorage} from "../../../hooks/useLocalStorage";
import {useData} from "../../../hooks/useData";
import {doFetch} from "../../../util/fetch";
import {postBody} from "../../../util/requestBody";
import SvgLoader from "../../../svg/SvgLoader";
import {LucidePlus} from "lucide-react";
import Toggle from "../../../components/Toggle";
import {useOverlay} from "../overlay/OverlayContext";

export default function ConsoleFilterOverlay(props: { hide: () => void }){

    const [consoleFilters, setConsoleFilters] = useState<ConsoleFilter[]>([])
    const [error, setError] = useState<string>()
    const [errors, setErrors] = useState<ConsoleFilterError[]>([])

    const [serverId, ] = useLocalStorage("serverId");

    const filterData = useData<GetConsoleFiltersResponse>(`/api/client/console/filters?serverId=${serverId}`);

    const overlay = useOverlay()

    useEffect(() => {
        if (filterData.data) {
            setConsoleFilters(filterData.data?.filters)
        }
    }, [filterData.data]);

    function updateFilter(i: number, type?: number, ignoreCase?: boolean, input?: string, enabled?: boolean) {
        const newFilters = [...consoleFilters]
        const filter = newFilters[i]

        if (type) {
            filter.type = type
        }
        if (ignoreCase !== undefined) {
            filter.ignoreCase = ignoreCase
        }
        if (input) {
            filter.filter = input
        }
        if (enabled !== undefined) {
            filter.enabled = enabled
        }

        setConsoleFilters(newFilters)
    }

    function deleteFilter(i: number) {
        const newFilters = [...consoleFilters]
        newFilters.splice(i, 1)
        setConsoleFilters(newFilters)
    }

    function saveFilters() {
        doFetch<PostConsoleFilterResponse>("/api/client/console/filters", postBody({
            serverId: serverId,
            filters: consoleFilters,
        })).then(result => {
            if (result.data) {
                if (result.data.ok){
                    closeFilters()
                }
                else{
                    setError("One or more filters are invalid")
                    setErrors(result.data.errors)
                }
            } else {
                if (result.error && result.error.code === 67){
                    overlay.openErrorOverlay(result.error)
                }
                else{
                    setError(result.errorString ?? "Unknown error")
                }
            }
        })
    }

    function closeFilters() {
        setError(undefined)
        props.hide()
    }

    function addFilter(){
        setConsoleFilters([...consoleFilters, {
            enabled: false,
            filter: "new filter",
            ignoreCase: false,
            type: ConsoleFilterType.String
        }])
    }

    return <OverlayBase hide={props.hide} w="max-w-6xl mx-12">
        <h2 className="text-xl font-bold mb-4">Message Filters</h2>

        <p className="mb-2">Console message filters are applied directly on the server. Messages filtered out by
            these rules will never appear in console logs for anyone. Only new console messages are affected by the filters.</p>

        {filterData.isLoading && (<div className="flex flex-col items-center">
            <SvgLoader/>
            <p className="mt-4 text-white text-lg font-semibold tracking-wide">Loading...</p>
        </div>)}
        {!filterData.isLoading && (<>
            <div className="mb-4 w-full">
                <button
                    className="px-4 py-2 rounded-md bg-primary hover:opacity-90 float-right inline-flex items-center"
                    onClick={addFilter}><LucidePlus size={16}/><span className="ms-2">Add filter</span></button>
                <table cellPadding="4" className="w-full">
                    <thead>
                    <tr>
                        <th></th>
                        <th>Filter Type</th>
                        <th>Ignore Case</th>
                        <th>Filter Input</th>
                        <th></th>
                    </tr>
                    </thead>
                    <tbody>
                    {consoleFilters && consoleFilters.map((filter, i) => {
                        const error = errors.find(x => x.index === i)?.error
                        console.log("error for", i, "is", error)
                        return <tr key={i}>
                            <td className="sm:pr-4">
                                <Toggle checked={filter.enabled}
                                    onChange={e => updateFilter(i, undefined, undefined, undefined, e.currentTarget.checked)}/>
                            </td>
                            <td className="block w-36">
                                <select value={filter.type}
                                   onChange={e => updateFilter(i, parseInt(e.currentTarget.value))}
                                   className="bg-neutral-600 text-white text-sm font-mono w-full pl-1 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary">
                                    <option value="0">Exact match</option>
                                    <option value="1">Starts with</option>
                                    <option value="2">Contains</option>
                                    <option value="3">Ends with</option>
                                    <option value="4">Regex</option>
                                </select>
                            </td>
                            <td className="text-center">
                                <input type="checkbox" checked={filter.ignoreCase}
                                   onChange={e => updateFilter(i, undefined, e.currentTarget.checked)}
                                   className="w-4 h-4 text-primary bg-neutral-600 border-0 "/>
                            </td>
                            <td className="w-full">
                                <input type="text" value={filter.filter}
                                  onChange={(e) => updateFilter(i, undefined, undefined, e.currentTarget.value)}
                                  className="bg-neutral-600 text-white text-sm font-mono w-full px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"/>
                                {error && <p className="text-red-500 px-2">{error}</p>}
                            </td>
                            <td>
                                <button className="bg-red-500 px-3 py-1 rounded-md hover:opacity-90 text-lg"
                                        onClick={() => deleteFilter(i)}>&times;</button>
                            </td>
                        </tr>
                    })}
                    </tbody>
                </table>
            </div>

            {(error || filterData.error) && (<p className="text-red-500 text-right mb-2">{error || filterData.error}</p>)}
            <div className="flex justify-end space-x-2">
                <button type="button" className="px-4 py-2 rounded-md bg-neutral-700 hover:opacity-90"
                        onClick={closeFilters}>
                    Cancel
                </button>
                <button type="button" className="px-4 py-2 rounded-md bg-primary hover:opacity-90"
                        onClick={saveFilters}>
                    Save
                </button>
            </div>
        </>)}
    </OverlayBase>

    function getError(index: number) {
        const error = errors.find(x => x.index === index);
        return error?.error
    }
}

interface GetConsoleFiltersResponse {
    filters: ConsoleFilter[]
}